// "Other lines", split into one collapsible group per kind of service.
//
// Pure, and generic: nothing here knows a city. A line belongs to the group of
// the service it is — its mode where it has one (trams, light rail), V/Line-
// style regional trains by their flag, everything else the city's trains — and
// a city names its two train groups itself (`lineGroups` in src/city.js:
// Melbourne's METRO and V/LINE, anywhere else TRAINS and REGIONAL).
//
// A row is counted by the word it wears (src/severity.js `statusWord`): a
// suspended line with buses standing in is BUSES, not SUSPENDED, so the header
// and the rows underneath always say the same thing.

import { prefs } from './core/storage.js';

export const TRAIN = 'train';
export const REGIONAL = 'regional';

/** The group a row belongs to. */
export function groupKeyOf(line) {
  if (line?.regional) return REGIONAL;
  return line?.mode || TRAIN;
}

/** What a row is counted as in the header, in the words the row itself wears. */
export function categoryOf(line) {
  if (line?.status === 'suspended') {
    if (line.replacement === 'diverted') return 'diverted';
    if (line.replacement === 'coaches') return 'coaches';
    if (line.replacement === 'buses') return 'buses';
    return 'suspended';
  }
  if (line?.status === 'delayed') return 'delayed';
  if (line?.status === 'planned') return 'planned';
  return 'good';
}

// Worst first, which is the order the header reads in.
const SUMMARY_ORDER = ['suspended', 'diverted', 'buses', 'coaches', 'delayed', 'planned'];
const SUMMARY_WORD = {
  suspended: 'SUSPENDED',
  diverted: 'DIVERTED',
  buses: 'BUSES',
  coaches: 'COACHES',
  delayed: 'DELAYED',
  planned: 'PLANNED WORKS',
};
/** Everything that is happening now, as opposed to planned works or nothing. */
const CURRENT = new Set(['suspended', 'diverted', 'buses', 'coaches', 'delayed']);

/** "3 PLANNED WORKS · 1 BUSES", or "ALL RUNNING" (or "TIMETABLE" where nothing is live). */
export function summaryOf(rows, { timetableOnly = false } = {}) {
  const counts = new Map();
  for (const row of rows) {
    const c = categoryOf(row);
    if (c !== 'good') counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  if (!counts.size) return timetableOnly ? 'TIMETABLE' : 'ALL RUNNING';
  return SUMMARY_ORDER.filter((c) => counts.has(c)).map((c) => `${counts.get(c)} ${SUMMARY_WORD[c]}`).join(' · ');
}

/** Is anything on these lines happening now (not merely planned)? */
export const hasCurrentTrouble = (rows) => rows.some((row) => CURRENT.has(categoryOf(row)));

/** The heading of a group: the city's name for its trains, else the mode's own. */
export function labelOf(key, { names = {}, modes = [] } = {}) {
  if (key === TRAIN) return names.train || 'TRAINS';
  if (key === REGIONAL) return names.regional || 'REGIONAL';
  const mode = modes.find((m) => m.id === key);
  return mode?.labels?.group || String(key).toUpperCase();
}

/**
 * The groups, in a fixed order: the city's declared main group, then trains,
 * regional, then the city's modes as it declares them. A kind this has never heard of goes last, under its own name.
 * Rows keep the order they arrive in (worst first, from the screen).
 */
export function groupRows(rows, { names = {}, modes = [], order = modes.map((m) => m.id) } = {}) {
  const by = new Map();
  for (const row of rows) {
    const key = groupKeyOf(row);
    if (!by.has(key)) by.set(key, []);
    by.get(key).push(row);
  }
  const rank = (key) => {
    // The city's declared main group leads, whatever kind it is (Canberra's
    // light rail would otherwise sit under REGIONAL).
    const known = [...(names.main ? [names.main] : []), TRAIN, REGIONAL, ...order];
    const i = known.indexOf(key);
    return i < 0 ? known.length : i;
  };
  return [...by.keys()]
    .sort((a, b) => rank(a) - rank(b))
    .map((key) => ({ key, label: labelOf(key, { names, modes }), rows: by.get(key) }));
}

/**
 * Open or closed when nobody has said: the main group (the trains) is open,
 * and so is any group with something happening now. The rest are collapsed:
 * a quiet group is one line, which is the point.
 */
export function defaultOpen(group, main) {
  return group.key === main || hasCurrentTrouble(group.rows);
}

/**
 * The group that is open unless told otherwise: the city's declared main
 * service (`lineGroups.main`, Canberra's light rail), else its trains, else the
 * group with the most lines.
 */
export function mainKey(groups, declared = null) {
  if (declared && groups.some((g) => g.key === declared)) return declared;
  if (groups.some((g) => g.key === TRAIN)) return TRAIN;
  return [...groups].sort((a, b) => b.rows.length - a.rows.length)[0]?.key ?? null;
}

/**
 * A group with more lines than this is SEARCH-FIRST: listing it would bury
 * everything under it (Melbourne has 376 bus routes), so its header opens
 * Choose lines, which shows what you watch, what is near you and a search,
 * instead of unfolding. Generic: any mode or city's group over the line.
 */
export const SEARCH_FIRST = 40;
export const isSearchFirst = (rows) => (Array.isArray(rows) ? rows.length : Number(rows)) > SEARCH_FIRST;

/**
 * What "Add buses" / "Add trams" scrolls to: the group's own heading, for every
 * mode alike.
 */
export const landingSelector = (mode) => `#${mode}-group`;

// Route order for numbered services (trams, buses).
/** Route number first (numeric), then its letter suffix: 600 < 623 < 900, 7 < 7A < 8. */
export function compareRoutes(a, b) {
  const sa = String(a?.short ?? a?.name ?? '');
  const sb = String(b?.short ?? b?.name ?? '');
  const na = parseInt(sa, 10);
  const nb = parseInt(sb, 10);
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
  if (Number.isFinite(na) !== Number.isFinite(nb)) return Number.isFinite(na) ? -1 : 1;
  return sa.localeCompare(sb, undefined, { numeric: true });
}

/** Search results with the route whose number IS the term first; the rest keep their order. */
export function leadWithNumber(rows, term) {
  const t = String(term ?? '').trim().toLowerCase();
  const exact = (r) => String(r?.short ?? '').toLowerCase() === t;
  return [...rows.filter(exact), ...rows.filter((r) => !exact(r))];
}

// ── Drawing a long list ──────────────────────────────────────────────────────
// The first screenful at once, the rest in chunks (the screens grow the budget
// while the browser is idle). Hundreds of rows never block the first paint.
export const FIRST_ROWS = 40;
export const CHUNK_ROWS = 80;

/**
 * Which rows to draw now: each group's rows in order until `budget` rows are
 * used. A group with `open: false` draws none and costs none.
 * @param {{key: string, rows: any[], open?: boolean}[]} groups
 * @returns {Record<string, any[]>}
 */
export function drawBudget(groups, budget) {
  let left = budget;
  const out = {};
  for (const g of groups) {
    const rows = g.open === false ? [] : g.rows.slice(0, Math.max(left, 0));
    left -= rows.length;
    out[g.key] = rows;
  }
  return out;
}

/** A group this small is shown open with no header to press: hiding it costs more than it saves. */
export const SMALL_GROUP = 2;
export const isSmall = (group) => group.rows.length <= SMALL_GROUP;

// ── Remembered ───────────────────────────────────────────────────────────────
//
// What somebody chose, per city and group. Only a choice is stored: a group
// they never touched keeps following the rule above, so a line going wrong
// still opens its group, and one they closed stays closed.

const KEY = 'lateagain:lines-open:v1';

const slot = (city, key) => `${city}:${key}`;

export function readOpenChoices() {
  const raw = prefs[KEY];
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? { ...raw } : {};
}

export function writeOpenChoice(city, key, open) {
  const next = { ...readOpenChoices(), [slot(city, key)]: Boolean(open) };
  prefs[KEY] = next;
  return next;
}

/** A stored choice, else the default. */
export function isOpen(choices, city, group, main) {
  if (isSmall(group)) return true;
  const stored = choices?.[slot(city, group.key)];
  return typeof stored === 'boolean' ? stored : defaultOpen(group, main);
}
